import { reactive } from "vue";
import { EventEmitter } from 'events';

/**
 * This is a generic base class for managing frontend data stores in legacy pages.
 * We can't use pinia directly in legacy pages so this class provides a simple interface
 * for storing, updating, and removing items.
 * 
 * In order to extend this with a type, do something like this:
 *
 * ```typescript - MyModelTypeStore.ts
 * import BaseStore from '@legacy-src/Datastores/BaseStore';
 * import { MyModelTypeInterface } from '@Classes/MyModelType';
 *
 * class MyModelTypeStore extends BaseStore<MyModelTypeInterface> {}
 *
 * export default new MyModelTypeStore();
 * ```
 * 
 * Note that the items in this store are tracked using vues 'reactive' system, so you can
 * use this store in your components and any changes to the items will be reactive. For use
 * outside of vue where reactivity isn't available, you can listen to the events emitted to
 * know when items are updated, removed, or cleared.
 * 
 * Note: I built this while there was a bug in transaction.js that was preventing me from using
 * pinia and I have since diagnosed that issue (see window.legacyDatastores in app.ts) but pinia
 * doesn't have a nice way to extend stores like this and this class is actually pretty nice to use
 * so I am keeping it. If you want to use pinia, you can - just make sure you do not import pinia
 * into transaction.js or it will break the legacy page.
 */

export default class BaseStore<T extends { id: number|null, group_index: number|null }> {
    items: T[];
    events: EventEmitter;
    primary_key_name: string; // because legacy tables use SYSID instead of id

    constructor(primary_key_name: string = 'id') {
        this.items = reactive([]); // Use Vue's reactive system to track changes
        this.events = new EventEmitter();
        this.primary_key_name = primary_key_name;
    }

    // Return an item from its id
    find(id: number|null): T | undefined {
        // cast to number for strict type checking
        if (typeof id !== 'number') {
            id = Number(id);
            if (isNaN(id)) {
                console.error('Invalid id provided to find method:', id);
                return undefined;
            }
        }

        // Find the item with the given id
        return this.items.find(item => item[this.primary_key_name] == id);
    }

    // pass an object with keys to return all items that match the key/values
    // this is good for ie finding relationships
    search(term: object, strict: boolean = false): T[] {
        return this.items.filter(item => {
            return Object.keys(term).every(key => {
                if (strict) {
                    return item[key] === term[key];
                } else {
                    return item[key] == term[key];
                }
            });
        });
    }

    // Store a single item in the store
    store(item: T) {
        if (!item) return;

        // If the item already exists, update it
        const existingItem = this.find(item[this.primary_key_name]);
        if (existingItem) {
            return this.update(item);
        } 
        
        // Otherwise, add it to the store
        this.items.push(item);

        // And emit an update event (the update call above will emit its own)
        this.events.emit('updated', item);

        // and return the item we just stored for the convenience
        return this.find(item[this.primary_key_name]);
    }

    // Store multiple items in the store
    patch(items: T[]) {
        if (!items) return;

        items.forEach(item => this.store(item));
    }

    // Update an existing item in the store
    update(record: T) {
        const local_item = this.find(record[this.primary_key_name]);

        if (!local_item) {
            console.error('Item not found in store - cannot update. Did you mean to use store?', record);
            return;
        }

        Object.assign(local_item, record);

        this.events.emit('updated', local_item); // Emit an update event

        // return the updated item
        return local_item;
    }

    // Remove items from the store
    remove(ids: number[]) {
        this.items = this.items.filter(item => !ids.includes(item[this.primary_key_name]));
        this.events.emit('removed', ids); // Emit a remove event
    }

    // Clear all items from the store
    clear() {
        this.items.length = 0; // Clear the array
        this.events.emit('cleared'); // Emit a clear event
    }

    // Replace the contents of the store with the incoming items - this will remove
    // any items that are in the store but not in the incoming items
    // This is useful for resetting the store to a known state
    wash(items: T[]) {
        this.items.length = 0; // Clear the array
        this.items.push(...items); // Add new items while keeping reactivity
        this.events.emit("washed", this.items);
    }
}